(function () {
  'use strict';

  const POLL_MS = 2500;
  const JOB_TIMEOUT_MS = 240000;
  const PAGE_SIZE = 12;
  const MAX_INPUT_BYTES = 100 * 1024 * 1024;
  const MAX_PREPARED_BYTES = 4 * 1024 * 1024;
  const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
  const canonicalSKU = value => String(value || '').trim().toUpperCase();
  const searchText = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const compactText = value => searchText(value).replace(/[^a-z0-9]/g, '');
  const usableColour = value => {
    const colour = String(value || '').trim().replace(/Biege & Tan/gi, 'Beige & Tan');
    return /^(?:TF|TCN)[\s-]*\d|^TF(?:SPC|GD|LL)/i.test(colour) ? '' : colour;
  };
  const safeURL = value => {
    const address = String(value || '').trim();
    if (!address) return '';
    try {
      const url = new URL(address, location.href);
      return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
    } catch { return ''; }
  };
  const readJSON = (root, selector) => {
    const script = root.querySelector(selector);
    return script ? JSON.parse(script.textContent) : null;
  };
  const prepareModels = catalog => {
    const source = Array.isArray(catalog) ? catalog : catalog?.models;
    if (!Array.isArray(source)) throw new Error('Flooring catalog is missing.');
    const seen = new Set();
    return source.filter(model => model && model.title && (model.normalized_sku || model.sku)).map(model => {
      const sku = canonicalSKU(model.normalized_sku || model.sku);
      if (seen.has(sku)) throw new Error('Flooring catalog has a duplicate SKU.');
      seen.add(sku);
      const textureURL = safeURL(model.texture?.url);
      return {
        ...model,
        normalized_sku: sku,
        series_key: String(model.series_tag || model.series_collection_handle || model.series || ''),
        colour: usableColour(model.colour),
        texture_url: textureURL,
        available: model.texture?.verified === true && !!textureURL,
        search: searchText([model.title, model.sku, sku, model.material_label, model.series, model.colour, model.finish, model.dimensions?.display].join(' ')),
        compact_search: compactText([model.title, model.sku, sku, model.dimensions?.display].join(' '))
      };
    });
  };

  class FlooringStudio {
    constructor(root) {
      this.root = root;
      this.locale = /^fr(?:[-_]|$)/i.test(root.dataset.locale || document.documentElement.lang || 'en') ? 'fr' : 'en';
      const translations = readJSON(root, 'script[data-flooring-i18n]');
      this.copy = translations?.cavador_flooring?.studio || translations;
      if (!this.copy || !this.copy.title || !this.copy.generate) throw new Error('Flooring translations are missing.');
      this.models = prepareModels(readJSON(root, 'script[data-flooring-catalog]'));
      this.eligible = this.models.filter(model => model.available);
      if (!this.eligible.length) throw new Error('Flooring catalog has no verified finishes.');
      const requestedSKU = new URL(location.href).searchParams.get('sku') || root.dataset.defaultSku || '';
      const requested = this.models.find(model => model.normalized_sku === canonicalSKU(requestedSKU));
      this.selected = requested || this.eligible.find(model => model.normalized_sku === 'TF8011') || this.eligible[0];
      this.filters = { search: '', material: requested ? requested.material : '', series: requested ? requested.series_key : '', colour: '' };
      this.visibleCount = PAGE_SIZE;
      this.orientation = 'lengthwise';
      this.image = null;
      this.result = null;
      this.pendingJob = null;
      this.busy = false;
      this.aiAvailable = false;
      this.availability = 'checking';
      this.disposed = false;
      this.jobsEndpoint = this.sameOriginEndpoint(root.dataset.jobsEndpoint || '/apps/cavador-visualizer/jobs');
      this.statusEndpoint = this.sameOriginEndpoint(root.dataset.statusEndpoint || '/apps/cavador-visualizer/status');
      this.sampleImage = safeURL(root.dataset.sampleImage);
      this.localeRoot = root.dataset.localeRoot ?? (location.pathname.startsWith('/fr/') ? '/fr' : '');
      this.uid = 'cfs-' + Math.random().toString(36).slice(2, 9);
      this.render();
      this.$ = selector => root.querySelector(selector);
      this.$$ = selector => [...root.querySelectorAll(selector)];
      this.renderFilterChoices();
      this.renderFinishes();
      this.refreshSelection();
      this.bind();
      if (requestedSKU && !requested) this.error(this.text('requested_unavailable'));
      this.checkAI();
    }

    text(key, values = {}) {
      return String(this.copy[key] || '').replace(/\{([a-z_]+)\}/g, (match, name) => values[name] == null ? match : String(values[name]));
    }
    html(key, values) { return escapeHTML(this.text(key, values)); }
    sameOriginEndpoint(value) {
      const url = new URL(value, location.href);
      if (url.origin !== location.origin) throw new Error('Flooring endpoints must use the storefront app proxy.');
      return url.href;
    }
    finishName(model) {
      return String(model.title).replace(/ (?:Waterproof SPC Vinyl Plank|SPC Vinyl|Laminate|Loose Lay Vinyl|Glue-Down Vinyl|Engineered Hardwood) Flooring$/, '');
    }
    materialName(material) { return this.text('material_' + String(material).replace(/-/g, '_')) || this.models.find(model => model.material === material)?.material_label || material; }
    directionName(direction) { return this.text(direction === 'widthwise' ? 'direction_across' : 'direction_along'); }
    productURL(model) {
      const path = String(model.url || '/products/' + model.handle);
      return path.startsWith('/products/') ? this.localeRoot.replace(/\/$/, '') + path : safeURL(path);
    }
    controlsLocked() { return this.busy || !!this.pendingJob; }
    resultIsCurrent() { return !!this.result && this.result.product.id === this.selected.normalized_sku && this.result.orientation === this.orientation; }

    render() {
      const id = name => this.uid + '-' + name;
      this.root.innerHTML = `
        <div class="cfs-intro"><div><p class="cfs-eyebrow">${this.html('eyebrow')}</p><h1 class="cfs-title">${this.html('title')}</h1></div><p>${this.html('intro')}</p></div>
        <div class="cfs-workspace">
          <div class="cfs-stage-col">
            <div class="cfs-stage-top"><ol class="cfs-stepbar" aria-label="${this.html('steps_label')}"><li data-step="1"><b>01</b> ${this.html('step_photo')}</li><li data-step="2"><b>02</b> ${this.html('step_finish')}</li><li data-step="3"><b>03</b> ${this.html('step_preview')}</li></ol><button type="button" class="cfs-text-btn" data-action="replace" hidden>${this.html('change_photo')}</button></div>
            <p class="cfs-error" role="alert" hidden></p>
            <div class="cfs-stage">
              <div class="cfs-drop"><svg class="cfs-upload-icon" viewBox="0 0 48 56" fill="none" aria-hidden="true"><path d="M8 8h23l9 9v31H8z" stroke="currentColor"/><path d="M31 8v10h9M24 38V23m-6 6 6-6 6 6" stroke="currentColor"/></svg><h2>${this.html('drop_title')}</h2><p>${this.html('drop_text')}</p><button type="button" class="cfs-btn" data-action="upload">${this.html('upload')} </button><button type="button" class="cfs-text-btn" data-action="sample"${this.sampleImage ? '' : ' hidden'}>${this.html('sample')}</button><p class="cfs-filehint">${this.html('file_hint')}</p></div>
              <div class="cfs-image-wrap" hidden><img class="cfs-room-image" alt="${this.html('original_alt')}"><img class="cfs-after" alt="${this.html('preview_alt')}" hidden><span class="cfs-badge" hidden>${this.html('before')}</span><span class="cfs-badge cfs-badge-after" hidden>${this.html('after')}</span><div class="cfs-compare-line" hidden></div><div class="cfs-compare-knob" hidden aria-hidden="true">‹ ›</div></div>
              <div class="cfs-loading" role="status" aria-live="polite" hidden><div class="cfs-spinner" aria-hidden="true"></div><span>${this.html('creating')}</span><small>${this.html('processing')}</small></div>
            </div>
            <div class="cfs-stage-footer"><p class="cfs-hint" aria-live="polite"></p><div class="cfs-compare-controls" hidden><label for="${id('compare')}">${this.html('before')}</label><input id="${id('compare')}" type="range" min="0" max="100" value="50" aria-label="${this.html('comparison')}"><span aria-hidden="true">${this.html('after')}</span><button type="button" class="cfs-text-btn" data-action="download">${this.html('save')}</button></div></div>
          </div>
          <aside class="cfs-sidebar" aria-label="${this.html('selection')}">
            <p class="cfs-eyebrow">${this.html('collection')}</p><h2>${this.html('choose_finish')}</h2>
            <div class="cfs-finder"><label for="${id('search')}">${this.html('search_label')}</label><input id="${id('search')}" data-filter="search" type="search" placeholder="${this.html('search_placeholder')}" autocomplete="off"><div class="cfs-filter-grid"><div><label for="${id('material')}">${this.html('material')}</label><select id="${id('material')}" data-filter="material"></select></div><div><label for="${id('series')}">${this.html('series')}</label><select id="${id('series')}" data-filter="series"></select></div><div class="cfs-colour-filter"><label for="${id('colour')}">${this.html('colour')}</label><select id="${id('colour')}" data-filter="colour"></select></div></div><div class="cfs-filter-meta"><p data-count role="status" aria-live="polite"></p><button type="button" class="cfs-text-btn" data-action="clear">${this.html('clear_filters')}</button></div></div>
            <ul class="cfs-finish-grid" role="list" aria-label="${this.html('finish_list')}"></ul><p class="cfs-no-results" hidden>${this.html('no_results')}</p><button type="button" class="cfs-more" data-action="more" hidden>${this.html('load_more')}</button>
            <div class="cfs-product-detail"><div class="cfs-selected"><div class="cfs-selected-image"></div><div><h3 class="cfs-selected-name"></h3><p class="cfs-selected-sku"></p></div></div><p class="cfs-detail-meta"></p><p class="cfs-dimensions"></p>
              <fieldset class="cfs-field"><legend>${this.html('direction')}</legend><div class="cfs-toggle"><button type="button" data-orientation="lengthwise" aria-pressed="true">${this.html('direction_along')}</button><button type="button" data-orientation="widthwise" aria-pressed="false">${this.html('direction_across')}</button></div></fieldset>
              <p class="cfs-ai-note" id="${id('privacy')}">${this.html('privacy')}</p><button type="button" class="cfs-btn cfs-render" data-action="generate" aria-describedby="${id('privacy')} ${id('service')}" disabled><span class="cfs-generate-label">${this.html('generate')}</span></button><p class="cfs-service-status" id="${id('service')}" role="status" aria-live="polite"></p><a class="cfs-product-link"></a>
            </div>
          </aside>
        </div>
        <div class="cfs-lower"><div><h2>${this.html('lower_photo_title')}</h2><p>${this.html('lower_photo')}</p></div><div><h2>${this.html('lower_finish_title')}</h2><p>${this.html('lower_finish')}</p></div><div><h2>${this.html('lower_next_title')}</h2><p>${this.html('lower_next')}</p></div></div>
        <input type="file" data-file accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif" hidden>
      `;
    }

    filterBase(includeSeries = true) {
      return this.models.filter(model => (!this.filters.material || model.material === this.filters.material) && (!includeSeries || !this.filters.series || model.series_key === this.filters.series));
    }
    filteredModels() {
      const query = searchText(this.filters.search).trim();
      const compactQuery = compactText(query);
      return this.filterBase().filter(model => (!this.filters.colour || model.colour === this.filters.colour) && (!query || model.search.includes(query) || compactQuery && model.compact_search.includes(compactQuery)));
    }
    fillSelect(key, entries, allLabel) {
      const select = this.$(`[data-filter="${key}"]`);
      select.innerHTML = `<option value="">${escapeHTML(allLabel)}</option>` + entries.map(([value, label]) => `<option value="${escapeHTML(value)}">${escapeHTML(label)}</option>`).join('');
      select.value = this.filters[key];
    }
    renderFilterChoices() {
      const materials = [...new Set(this.models.map(model => model.material).filter(Boolean))].map(value => [value, this.materialName(value)]);
      this.fillSelect('material', materials, this.text('all_materials'));
      const seriesMap = new Map(this.filterBase(false).filter(model => model.series_key).map(model => [model.series_key, model.series]));
      if (!seriesMap.has(this.filters.series)) this.filters.series = '';
      this.fillSelect('series', [...seriesMap].sort((a, b) => String(a[1]).localeCompare(String(b[1]), this.locale, { numeric: true })), this.text('all_series'));
      const colours = [...new Set(this.filterBase().map(model => model.colour).filter(Boolean))].sort((a, b) => a.localeCompare(b, this.locale));
      if (!colours.includes(this.filters.colour)) this.filters.colour = '';
      this.fillSelect('colour', colours.map(value => [value, value]), this.text('all_colours'));
    }
    renderFinishes() {
      const matches = this.filteredModels();
      const grid = this.$('.cfs-finish-grid');
      const scrollTop = grid.scrollTop;
      grid.innerHTML = matches.slice(0, this.visibleCount).map(model => `<li><button type="button" class="cfs-finish" data-sku="${escapeHTML(model.normalized_sku)}" aria-pressed="${model.normalized_sku === this.selected.normalized_sku}" aria-label="${this.html(model.available ? 'choose_model' : 'unavailable_model', { name: this.finishName(model), sku: model.sku || model.normalized_sku })}"${!model.available || this.controlsLocked() ? ' disabled' : ''}><span class="cfs-swatch">${model.available ? `<img src="${escapeHTML(model.texture_url)}" alt="" loading="lazy" decoding="async"><span class="cfs-check" aria-hidden="true">✓</span>` : `<span class="cfs-image-pending">${this.html('image_pending')}</span>`}</span><span class="cfs-finish-name">${escapeHTML(this.finishName(model))}</span><span class="cfs-finish-sku">${escapeHTML(model.sku || model.normalized_sku)}</span></button></li>`).join('');
      grid.scrollTop = scrollTop;
      this.$('.cfs-no-results').hidden = matches.length > 0;
      this.$('[data-action="more"]').hidden = matches.length <= this.visibleCount;
      this.$('[data-count]').textContent = this.text('finish_count', { count: matches.length, available: matches.filter(model => model.available).length });
      this.$('[data-action="clear"]').hidden = !Object.values(this.filters).some(Boolean);
      this.bindImageErrors(grid);
    }
    bindImageErrors(scope) {
      scope.querySelectorAll('img').forEach(image => {
        const failed = () => { image.hidden = true; image.parentElement.classList.add('cfs-image-failed'); };
        image.addEventListener('error', failed, { once: true });
        if (image.complete && !image.naturalWidth) failed();
      });
    }
    refreshSelection() {
      this.$$('.cfs-finish').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.sku === this.selected.normalized_sku)));
      this.$('.cfs-selected-name').textContent = this.finishName(this.selected);
      this.$('.cfs-selected-sku').textContent = this.selected.sku || this.selected.normalized_sku;
      this.$('.cfs-selected-image').innerHTML = this.selected.available ? `<img src="${escapeHTML(this.selected.texture_url)}" alt="" loading="lazy" decoding="async">` : `<span class="cfs-image-pending">${this.html('image_pending')}</span>`;
      this.bindImageErrors(this.$('.cfs-selected-image'));
      this.$('.cfs-detail-meta').textContent = [this.materialName(this.selected.material), this.selected.series, this.selected.colour].filter(Boolean).join(' · ');
      this.$('.cfs-dimensions').textContent = this.selected.dimensions?.verified && this.selected.dimensions.display ? this.selected.dimensions.display : this.text('dimensions_unknown');
      this.$$('[data-orientation]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.orientation === this.orientation)));
      const link = this.$('.cfs-product-link');
      link.href = this.productURL(this.selected);
      link.textContent = this.text('explore', { name: this.finishName(this.selected) });
      this.update();
    }

    bind() {
      this.root.addEventListener('click', event => {
        const target = event.target.closest('button');
        if (!target || !this.root.contains(target) || target.disabled || this.busy) return;
        const action = target.dataset.action;
        if (action === 'generate') { this.generate(); return; }
        if (this.controlsLocked()) return;
        if (action === 'upload' || action === 'replace') this.$('[data-file]').click();
        if (action === 'sample' && this.sampleImage) this.loadPhoto(this.sampleImage, true);
        if (action === 'download') this.download();
        if (action === 'clear') {
          this.filters = { search: '', material: '', series: '', colour: '' };
          this.$('[data-filter="search"]').value = '';
          this.visibleCount = PAGE_SIZE;
          this.renderFilterChoices();
          this.renderFinishes();
          this.$('[data-filter="search"]').focus();
        }
        if (action === 'more') {
          const previousCount = this.visibleCount;
          this.visibleCount += PAGE_SIZE;
          this.renderFinishes();
          if (target.hidden) this.$$('.cfs-finish').slice(previousCount).find(button => !button.disabled)?.focus();
        }
        if (target.dataset.sku) {
          const selected = this.models.find(model => model.normalized_sku === target.dataset.sku && model.available);
          if (!selected) return;
          this.selected = selected;
          this.error('');
          this.refreshSelection();
        }
        if (['lengthwise', 'widthwise'].includes(target.dataset.orientation)) {
          this.orientation = target.dataset.orientation;
          this.error('');
          this.refreshSelection();
        }
      });
      this.$$('[data-filter]').forEach(control => {
        control.addEventListener(control.type === 'search' ? 'input' : 'change', () => {
          if (this.controlsLocked()) return;
          this.filters[control.dataset.filter] = control.value;
          this.visibleCount = PAGE_SIZE;
          this.renderFilterChoices();
          this.renderFinishes();
        });
      });
      this.$('[data-file]').addEventListener('change', event => {
        const file = event.target.files[0];
        if (!file || this.controlsLocked()) return;
        this.loadFile(file).finally(() => { event.target.value = ''; });
      });
      const stage = this.$('.cfs-stage');
      stage.addEventListener('dragover', event => { event.preventDefault(); if (!this.controlsLocked()) stage.classList.add('cfs-dragover'); });
      stage.addEventListener('dragleave', () => stage.classList.remove('cfs-dragover'));
      stage.addEventListener('drop', event => {
        event.preventDefault();
        stage.classList.remove('cfs-dragover');
        if (!this.controlsLocked() && event.dataTransfer.files[0]) this.loadFile(event.dataTransfer.files[0]);
      });
      this.$('.cfs-compare-controls input').addEventListener('input', event => this.compare(Number(event.target.value)));
      const wrapper = this.$('.cfs-image-wrap');
      const compareAt = event => {
        const bounds = wrapper.getBoundingClientRect();
        if (bounds.width) this.compare(100 - (event.clientX - bounds.left) / bounds.width * 100);
      };
      wrapper.addEventListener('pointerdown', event => {
        if (!this.result || this.controlsLocked() || event.button !== 0) return;
        this.compareDragging = true;
        wrapper.setPointerCapture(event.pointerId);
        compareAt(event);
      });
      wrapper.addEventListener('pointermove', event => { if (this.compareDragging) compareAt(event); });
      const endDrag = () => { this.compareDragging = false; };
      wrapper.addEventListener('pointerup', endDrag);
      wrapper.addEventListener('pointercancel', endDrag);
      wrapper.addEventListener('lostpointercapture', endDrag);
      wrapper.querySelectorAll('img').forEach(image => image.addEventListener('dragstart', event => event.preventDefault()));
    }

    error(message) {
      this.$('.cfs-error').textContent = message;
      this.$('.cfs-error').hidden = !message;
    }
    async checkAI() {
      try {
        const response = await fetch(this.statusEndpoint, { credentials: 'same-origin', signal: AbortSignal.timeout(10000) });
        const data = await response.json();
        this.aiAvailable = response.ok && data.aiAvailable === true && data.automatic === true && data.asyncJobs === true && data.flooringAvailable === true;
      } catch { this.aiAvailable = false; }
      this.availability = this.aiAvailable ? 'available' : 'unavailable';
      if (!this.disposed) this.update();
    }
    async loadFile(file) {
      if (this.controlsLocked()) return;
      const type = String(file.type || '').trim().toLowerCase();
      const extension = /\.([a-z0-9]+)$/i.exec(String(file.name || '').trim())?.[1]?.toLowerCase() || '';
      const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      const supportedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
      if (extension === 'dng' || (type ? !supportedTypes.includes(type) : !supportedExtensions.includes(extension))) { this.error(this.text('invalid_type')); return; }
      if (file.size > MAX_INPUT_BYTES) { this.error(this.text('too_large')); return; }
      const isHeif = type ? ['image/heic', 'image/heif'].includes(type) : ['heic', 'heif'].includes(extension);
      const url = URL.createObjectURL(file);
      try { await this.loadPhoto(url, false, isHeif); } finally { URL.revokeObjectURL(url); }
    }
    loadImage(url) {
      return new Promise((resolve, reject) => {
        if (typeof url !== 'string' || !/^(?:https?:\/\/|blob:|data:image\/(?:png|jpeg|webp);base64,)/i.test(url)) { reject(new Error(this.text('missing_image'))); return; }
        const image = new Image();
        if (/^https?:/i.test(url)) image.crossOrigin = 'anonymous';
        const timer = setTimeout(() => { image.onload = image.onerror = null; reject(new Error(this.text('image_timeout'))); }, 30000);
        image.onload = () => { clearTimeout(timer); resolve(image); };
        image.onerror = () => { clearTimeout(timer); reject(new Error(this.text('cannot_open'))); };
        image.src = url;
      });
    }
    encodeJPEG(canvas, quality) {
      return new Promise((resolve, reject) => {
        try {
          canvas.toBlob(blob => {
            if (!blob || blob.type !== 'image/jpeg') { reject(new Error(this.text('cannot_prepare'))); return; }
            resolve(blob);
          }, 'image/jpeg', quality);
        } catch { reject(new Error(this.text('cannot_prepare'))); }
      });
    }
    readPreparedImage(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result !== 'string' || !reader.result.startsWith('data:image/jpeg;base64,')) { reject(new Error(this.text('cannot_prepare'))); return; }
          resolve(reader.result);
        };
        reader.onerror = reader.onabort = () => reject(new Error(this.text('cannot_prepare')));
        reader.readAsDataURL(blob);
      });
    }
    async preparePhoto(image) {
      const canvas = document.createElement('canvas');
      const ratio = Math.min(1, 1800 / Math.max(image.naturalWidth, image.naturalHeight));
      if (Math.round(image.naturalWidth * ratio) < 64 || Math.round(image.naturalHeight * ratio) < 64) throw new Error(this.text('too_narrow'));
      for (const scale of [1, .8, .64]) {
        canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio * scale));
        if (canvas.width < 64 || canvas.height < 64) break;
        const context = canvas.getContext('2d');
        if (!context) throw new Error(this.text('cannot_prepare'));
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        for (const quality of [.9, .82, .74]) {
          const blob = await this.encodeJPEG(canvas, quality);
          if (blob.size <= MAX_PREPARED_BYTES) return { source: await this.readPreparedImage(blob), width: canvas.width, height: canvas.height };
        }
      }
      throw new Error(this.text('cannot_prepare'));
    }
    async loadPhoto(url, sample, isHeif = false) {
      if (this.controlsLocked()) return;
      this.error('');
      this.busy = true;
      this.$('.cfs-loading span').textContent = this.text('preparing');
      this.$('.cfs-loading small').textContent = this.text('opening');
      this.$('.cfs-loading').hidden = false;
      this.update();
      try {
        let image;
        try { image = await this.loadImage(url); } catch (error) { throw new Error(isHeif ? this.text('heif_unavailable') : error.message || this.text('cannot_open')); }
        if (image.naturalWidth < 200 || image.naturalHeight < 200) throw new Error(this.text('too_small'));
        if (image.naturalWidth * image.naturalHeight > 60000000) throw new Error(this.text('too_many_pixels'));
        const prepared = await this.preparePhoto(image);
        const normalized = await this.loadImage(prepared.source);
        if (this.disposed) return;
        this.image = normalized;
        this.hideResult();
        this.$('.cfs-room-image').src = normalized.src;
        this.$('.cfs-room-image').alt = this.text(sample ? 'sample_alt' : 'uploaded_alt');
        this.$('.cfs-drop').hidden = true;
        this.$('.cfs-image-wrap').style.setProperty('--cfs-photo-width', (640 * prepared.width / prepared.height) + 'px');
        this.$('.cfs-image-wrap').hidden = false;
        this.$('[data-action="replace"]').hidden = false;
      } catch (error) {
        if (!this.disposed) this.error(error.message || this.text('cannot_open'));
      } finally {
        this.busy = false;
        if (!this.disposed) { this.$('.cfs-loading').hidden = true; this.update(); }
      }
    }
    update() {
      const locked = this.controlsLocked();
      this.root.setAttribute('aria-busy', String(this.busy));
      this.$$('button:not([data-action="generate"]),input,select').forEach(control => {
        const model = control.dataset.sku && this.models.find(item => item.normalized_sku === control.dataset.sku);
        control.disabled = locked || !!model && !model.available;
      });
      const generate = this.$('[data-action="generate"]');
      generate.disabled = this.busy || !this.pendingJob && (!this.image || !this.aiAvailable || !this.selected.available);
      this.$('.cfs-generate-label').textContent = this.text(this.busy ? 'wait' : this.pendingJob ? 'check_preview' : this.result ? 'generate_new' : 'generate');
      this.$('.cfs-compare-controls').hidden = !this.result;
      this.$('.cfs-image-wrap').classList.toggle('has-result', !!this.result);
      this.$$('[data-step]').forEach(step => {
        const active = Number(step.dataset.step) === (this.result ? 3 : this.image ? 2 : 1);
        step.classList.toggle('is-active', active);
        if (active) step.setAttribute('aria-current', 'step'); else step.removeAttribute('aria-current');
      });
      this.$('.cfs-service-status').textContent = this.text(!this.selected.available ? 'reference_pending' : this.pendingJob && !this.busy ? 'pending_status' : this.availability === 'checking' ? 'checking' : !this.aiAvailable ? 'unavailable' : 'available');
      this.$('.cfs-hint').textContent = this.busy ? this.text('busy_hint') : this.pendingJob ? this.text('pending_hint') : this.result && !this.resultIsCurrent() ? this.text('old_result', { previous: this.result.product.name, previous_direction: this.directionName(this.result.orientation), selected: this.finishName(this.selected), direction: this.directionName(this.orientation) }) : this.result ? this.text('result_hint', { name: this.result.product.name, direction: this.directionName(this.result.orientation) }) : this.image ? this.text('ready_hint') : this.text('start_hint');
    }
    hideResult() {
      this.result = null;
      this.$('.cfs-after').hidden = true;
      this.$$('.cfs-badge,.cfs-compare-line,.cfs-compare-knob').forEach(element => { element.hidden = true; });
    }
    showResult(url, job) {
      this.result = { url, product: job.product, orientation: job.orientation };
      this.$('.cfs-after').src = url;
      this.$('.cfs-after').alt = this.text('result_alt', { name: job.product.name, direction: this.directionName(job.orientation) });
      this.$('.cfs-after').hidden = false;
      this.$$('.cfs-badge,.cfs-compare-line,.cfs-compare-knob').forEach(element => { element.hidden = false; });
      this.compare(50);
    }
    compare(value) {
      const after = Number.isFinite(Number(value)) ? Math.max(0, Math.min(100, Number(value))) : 50;
      const divider = 100 - after;
      const slider = this.$('.cfs-compare-controls input');
      slider.value = after;
      slider.setAttribute('aria-valuetext', after === 0 ? this.text('original_value') : after === 100 ? this.text('preview_value') : this.text('percentage', { value: Math.round(after) }));
      this.$('.cfs-after').style.clipPath = `inset(0 0 0 ${divider}%)`;
      this.$('.cfs-compare-line').style.left = divider + '%';
      this.$('.cfs-compare-knob').style.left = divider + '%';
    }
    download() {
      if (!this.result || this.controlsLocked()) return;
      const link = document.createElement('a');
      link.href = this.result.url;
      link.download = `cavador-${this.result.product.id.toLowerCase()}-${this.result.orientation}-floor-preview.png`;
      link.click();
    }
    serviceError(data, fallback) { return this.locale === 'fr' ? fallback : typeof data.error === 'string' ? data.error : data.error?.message || fallback; }
    async readResponse(response) {
      let data;
      try { data = await response.json(); } catch { throw new Error(this.text('unexpected_response')); }
      if (!response.ok) {
        const error = new Error(this.serviceError(data, this.text('service_unavailable')));
        error.status = response.status;
        throw error;
      }
      return data;
    }
    async waitForJob(job, deadline) {
      while (Date.now() < deadline && !this.disposed) {
        const response = await fetch(job.pollUrl, { credentials: 'same-origin', signal: AbortSignal.timeout(Math.max(1, Math.min(30000, deadline - Date.now()))) });
        const data = await this.readResponse(response);
        if (data.status === 'completed') {
          if (data.mode !== 'automatic' || data.requiresCompositing !== false || data.surface !== 'floor' || data.family !== 'flooring' || data.product?.id !== job.product.id || data.product?.name !== job.requestProductName || data.orientation !== job.orientation) {
            this.pendingJob = null;
            throw new Error(this.text('unsupported_result'));
          }
          if (typeof data.image !== 'string' || !data.image) { this.pendingJob = null; throw new Error(this.text('finished_without_image')); }
          return data;
        }
        if (data.status === 'failed') { this.pendingJob = null; throw new Error(this.serviceError(data, this.text('failed_preview'))); }
        if (!['running', 'queued', 'pending'].includes(data.status)) throw new Error(this.text('unconfirmed_status'));
        const remaining = deadline - Date.now();
        if (remaining <= 0) break;
        await new Promise(resolve => setTimeout(resolve, Math.min(POLL_MS, remaining)));
      }
      const error = new Error(this.text('slow_preview'));
      error.name = 'PreviewTimeoutError';
      throw error;
    }
    async generate() {
      if (this.disposed || this.busy || !this.pendingJob && (!this.image || !this.aiAvailable || !this.selected.available)) return;
      this.error('');
      this.busy = true;
      this.$('.cfs-loading span').textContent = this.text(this.pendingJob ? 'checking_preview' : 'creating');
      this.$('.cfs-loading small').textContent = this.text('processing');
      this.$('.cfs-loading').hidden = false;
      this.update();
      try {
        const deadline = Date.now() + JOB_TIMEOUT_MS;
        if (!this.pendingJob) {
          const request = { image: this.image.src, surface: 'floor', family: 'flooring', product: { id: this.selected.normalized_sku, name: this.selected.title }, orientation: this.orientation };
          const response = await fetch(this.jobsEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request), credentials: 'same-origin', signal: AbortSignal.timeout(30000) });
          const data = await this.readResponse(response);
          if (typeof data.jobId !== 'string' || !/^[A-Za-z0-9_-]{32}$/.test(data.jobId)) throw new Error(this.text('unconfirmed_request'));
          const pollURL = new URL(this.jobsEndpoint.replace(/\/$/, '') + '/' + encodeURIComponent(data.jobId), location.href);
          if (pollURL.origin !== location.origin) throw new Error(this.text('invalid_status_address'));
          this.pendingJob = { jobId: data.jobId, pollUrl: pollURL.href, product: { id: request.product.id, name: this.finishName(this.selected) }, requestProductName: request.product.name, orientation: request.orientation, width: this.image.naturalWidth, height: this.image.naturalHeight };
        }
        const job = this.pendingJob;
        const data = await this.waitForJob(job, deadline);
        const generated = await this.loadImage(data.image);
        if (this.disposed) return;
        const output = document.createElement('canvas');
        output.width = job.width;
        output.height = job.height;
        output.getContext('2d').drawImage(generated, 0, 0, output.width, output.height);
        const url = output.toDataURL('image/png');
        this.pendingJob = null;
        this.showResult(url, job);
      } catch (error) {
        if (error.status === 404 || error.status === 410) this.pendingJob = null;
        const message = error.name === 'TimeoutError' || error.name === 'PreviewTimeoutError' ? this.text(this.pendingJob ? 'still_processing' : 'request_timeout') : error.message || this.text('generation_failed');
        if (!this.disposed) this.error(message);
      } finally {
        this.busy = false;
        if (!this.disposed) { this.$('.cfs-loading').hidden = true; this.update(); }
      }
    }
    dispose() { this.disposed = true; }
  }

  const initialize = scope => {
    scope.querySelectorAll('[data-flooring-studio]').forEach(root => {
      if (root.dataset.flooringStudioInitialized) return;
      try {
        const studio = new FlooringStudio(root);
        root.dataset.flooringStudioInitialized = 'true';
        root.cavadorFlooringStudio = studio;
      } catch (error) { console.error('Cavador flooring studio initialization:', error.message); }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true }); else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-flooring-studio]').forEach(root => root.cavadorFlooringStudio?.dispose()));
})();
